import type { CancelTokenSource, CancelToken } from 'axios';
import Axios from 'axios';
import type { CreateItemMultipartUploadResponseDto, UploadPartDto } from '../dto/uploadLocationDto';
import { invariant } from '../util/invariant';
import type { FilesystemApis } from '../upload/fileOps';
import type { FileApi } from '../upload/fileApi';
import { completeMultipartUpload, createItemUploadPartsSignedUrls } from '../api/uploadApi';
import { AsyncIterableBuffer } from '../async/iterableBuffer';
import type { ReadableFile} from './readableFile';
import { makeReadableFile } from './readableFile';
import type { FileSetListener } from './uploadProgression';
import { OperationConclusion } from '../dto/progress';
import { ProgressBuilder, resilientlyConcludePartialTask } from '../async/progressBuilder';
import type { UploadManagerOptions } from './upload';
import type { PrioritySemaphore } from '../async/prioritySemaphore';
import https from 'https';
import http from 'http';
import type { TaskContext } from '../async/taskContext';
import { UPLOAD_MULTIPART_THRESHOLD } from './limits';
import sortBy from 'lodash/sortBy';
import { redactUrl } from '../format/redactUrl';
import { exceptionToString } from '../util/exception';

const UPLOAD_FETCH_URL_COUNT = 10;

const MAX_CONCURRENT_PUTS = 4;

export class MultiPartUploader {
  private cancelTokenSource: CancelTokenSource;
  private rfile: ReadableFile;
  private progressBuilder: ProgressBuilder;

  constructor(
    private readonly options: UploadManagerOptions,
    private readonly itemId: string,
    private readonly uploadConfig: CreateItemMultipartUploadResponseDto,
    file: FileApi,
    fsApis: FilesystemApis | undefined,
    private readonly fsSemaphore: PrioritySemaphore,
    listener: FileSetListener | null,
    private taskContext: TaskContext
  ) {
    const partSize = this.uploadConfig.uploadPartSize;
    if (partSize > UPLOAD_MULTIPART_THRESHOLD) {
      this.options.getLogger().log({
        level: 'warn',
        message: 'Too large part size',
        scope: 'MultiPartUploader/constructor',
        partSize,
        UPLOAD_MULTIPART_THRESHOLD
      });
    }
    this.cancelTokenSource = Axios.CancelToken.source();
    this.rfile = makeReadableFile(file, fsApis);
    this.progressBuilder = new ProgressBuilder({
      files: {
        done: 0,
        known: 1,
      },
      bytes: {
        done: 0,
        known: this.rfile.size
      },
      status: 'Chunking file',
      description: 'Chunking ' + this.rfile.title + ' for upload',
      errors: [],
      percentage: 0,
      uploadMethods: { [itemId]: 'httpMultiPart' },
      conclusion: OperationConclusion.PENDING
    }, listener, this.cancelTokenSource.token);

  }
  public async startUpload() {
    this.cancelTokenSource.token.throwIfRequested();
    this.progressBuilder.fire();
    await this.progressBuilder.concludeTask('Uploaded file', 'Upload of ' + this.rfile.title, async () => {

      let uploadPartsArray: UploadPartDto[] = [];
      let error: any;
      try {
        uploadPartsArray = await this.uploadAllFilePartBatches();
      } catch(err) {
        error = err;
      }

      await this.rfile.close();

      const cancelled = Axios.isCancel(error);
      if (cancelled || !error) {
        const text = cancelled
          ? { status: 'Cancelled', activity: 'Stopping multipart upload of ' + this.rfile.title }
          : { status: 'File uploaded', activity: 'Assembing uploaded parts of ' + this.rfile.title };

        await this.progressBuilder.concludePartialTask(text.status, text.activity, () =>
          // Note: Not retryable. Should not be cancelled.
          completeMultipartUpload(
            this.options.client,
            this.itemId,
            this.uploadConfig.uploadLocationId,
            this.uploadConfig.targetKey,
            this.uploadConfig.uploadId,
            cancelled,
            uploadPartsArray,
            { priority: this.taskContext.priority }
          ));
      }

      if (error)
        throw error;

      this.progressBuilder.progress.files.done = 1;
    });
  }

  private async* getSignedUrls(): AsyncIterable<SignedUrl> {
    let index = 0;
    let completedParts = 0;

    const cancelToken = this.cancelTokenSource.token;

    // The following loop will fetch the specified number of upload urls until
    // we have all. (this.partCount)
    const { uploadPartCount: partCount, uploadId, uploadLocationId, targetKey } = this.uploadConfig;
    while (index < partCount) {
      cancelToken.throwIfRequested();

      // Fetch a new batch of urls. Fetch urls for all parts.
      let currentPartCount = UPLOAD_FETCH_URL_COUNT;

      // Last batch might not need currentPartCount parts to complete upload
      if (index + currentPartCount > partCount) {
        currentPartCount = partCount - index;
      }

      this.progressBuilder.fireActivity('Reserving batch', 'Reserving upload location for incremental upload of ' + this.rfile.title);
      const uploadPartsResponse = await this.taskContext.resilience.run(this.taskContext, async () => {
        return await createItemUploadPartsSignedUrls(
          this.options.client,
          this.itemId,
          {
            uploadLocationId: uploadLocationId,
            targetKey: targetKey,
            uploadId: uploadId,
            firstPartNumber: index + 1,
            partCount: currentPartCount,
          },
          cancelToken,
          this.taskContext.priority
        );
      });

      if (!uploadPartsResponse.uploadPartsSignedUrls || uploadPartsResponse.uploadPartsSignedUrls.length === 0) {
        throw new Error('Could not obtain upload locations.');
      }

      const signedUrls = uploadPartsResponse.uploadPartsSignedUrls;
      const partsInBatch = signedUrls.length;

      this.progressBuilder.fireActivity('Reserved batch', 'Reserved upload location incremental upload of file ' + this.rfile.title);
      for (const signedUrl of signedUrls) {
        yield { index, signedUrl };
        ++index;
      }

      completedParts += partsInBatch;
      invariant(index == completedParts, 'inconsistent completed parts');
    }
  }

  private async uploadAllFilePartBatches(): Promise<UploadPartDto[]> {
    const cancelToken = this.cancelTokenSource.token;
    cancelToken.throwIfRequested();
    /*
      Make sure to already have one presigned URL to work with before we attempt
      to acquire the file reading semaphore. We don't want to block others from
      reading files by acquiring the semaphore, only to start waiting for
      presigned URLs to use. Likewise, we don't want to wait until we've fetched
      all the presigned URLs before starting, nor do we want to pause uploading
      parts intermittently just to fetch more presigned URLs. Therefore create a
      buffer that can contain upto five presigned URLs and start prefilling it
      before acquiring the reading semaphore.
    */
    const urls = new AsyncIterableBuffer(5, this.getSignedUrls());
    await urls.prefill();

    this.progressBuilder.fireActivity('Upload queued', 'Upload of ' + this.rfile.title + ' will continue after other file reads finishes');
    let result: UploadPartDto[];
    try {
      result = await this.fsSemaphore.limit(() => this.uploadParts(urls, cancelToken), this.taskContext.priority, cancelToken);
    } finally {
      await urls.close();
    }
    return result;
  }

  private async uploadParts(urls: AsyncIterable<SignedUrl>, cancelToken: CancelToken): Promise<UploadPartDto[]> {
    const doneParts: UploadPartDto[] = [];

    const chunks = this.createUploadChunks(urls, cancelToken);
    // We get the wanted concurrency by creating n consumers
    // of the chunks AsyncIterable.
    const concurrency = Math.min(this.uploadConfig.uploadPartCount, MAX_CONCURRENT_PUTS);
    const uploadWorkers: Promise<void>[] = [];
    for (let i = 0; i < concurrency; i++) {
      uploadWorkers.push(this.uploadPartsSync(chunks, cancelToken, (part, bytes) => {
        doneParts.push(part);
      }));
    }
    await Promise.all(uploadWorkers);
    return sortBy(doneParts, 'PartNumber');
  }

  // Transforms the urls iterable to also include the
  // corresponding bytes to PUT.
  private async* createUploadChunks(
    urls: AsyncIterable<SignedUrl>,
    cancelToken: CancelToken,
  ): AsyncIterable<PartUpload> {
    const partSize = this.uploadConfig.uploadPartSize;
    for await (const url of urls) {
      cancelToken.throwIfRequested();
      const start = url.index * partSize;
      const end =  Math.min(start + partSize, this.rfile.size);
      const chunk = await this.rfile.getArrayBuffer(start, end, this.options.getLogger());
      yield {
        index: url.index,
        signedUrl: url.signedUrl,
        chunk: chunk,
      };
      cancelToken.throwIfRequested();
    }
  }

  private async uploadPartsSync(
    parts: AsyncIterable<PartUpload>,
    cancelToken: CancelToken,
    partDoneCb: (part: UploadPartDto, bytes: number) => void,
  ) {
    for await (const part of parts) {
      cancelToken.throwIfRequested();

      const etag = await resilientlyConcludePartialTask(
        this.progressBuilder,
        this.taskContext,
        'Uploaded part',
        'Uploading ' + this.rfile.title + ', part ' + (part.index + 1),
        () => this.uploadPart(part, cancelToken),
        this.options.getLogger()
      );

      partDoneCb({
        ETag: etag,
        PartNumber: part.index + 1
      }, part.chunk.byteLength);

      cancelToken.throwIfRequested();
    }
  }

  private async putNode(url: string, data: Uint8Array): Promise<string> {
    const logger = this.options.getLogger();
    return new Promise<string>((resolve, reject) => {
      const req = (url.startsWith('http://') ? http : https).request(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Content-Length': `${data.length}`
        },

        // This timeout is for socket inactivity, and applies both during
        // connection setup and after the connection is established.
        // Without this timeout, we've observed stale connections timeout
        // after 15 minutes.
        timeout: 60_000,
      });

      req.on('timeout', () => {
        // A timeout only triggers the 'timeout' event, it does not close the connection.
        logger.log({
          message: `Request to ${redactUrl(url)} timed out, destroying connection.`,
          level: 'warn',
        });
        reject(new Error('Request timed out'));
        req.destroy();
      });

      let etag: string | undefined;
      let error: Error | undefined;
      req.on('error', err => {
        logger.log({
          message: `Error on part PUT to ${redactUrl(url)}: ${exceptionToString(err)}`,
          level: 'warn',
        });
        error = err;
      });
      req.on('response', incoming => {
        if (!incoming.statusCode || incoming.statusCode < 200 || incoming.statusCode >= 300) {
          error = new Error(`Could not put chunk: ${incoming.statusMessage} (${incoming.statusCode})`);
        } else if (incoming.headers.etag) {
          etag = incoming.headers.etag as string;
        }
        // When adding a 'response' listener, it is required to consume
        // the response data for a 'close' event to be emitted.
        incoming.resume();
      });
      req.on('close', () => {
        if (etag) {
          resolve(etag);
        } else {
          logger.log({
            message: `Request to ${redactUrl(url)} failed: ${exceptionToString(error)}`,
            level: 'warn',
          });
          reject(error ?? 'Could not put chunk');
        }
      });
      req.write(data);
      req.end();
    });
  }


  private async putAxios(part: PartUpload, cancelToken: CancelToken): Promise<string> {
    let onUploadProgressBytes = 0;
    const onUploadProgress = (progressEvent: { loaded: number }) => {
      // The progressBuilder.progress may be shared/mutated by multiple
      // concurrent part uploads, so we must only add the bytes from
      // this PUT.
      const newBytesDone = progressEvent.loaded - onUploadProgressBytes;
      onUploadProgressBytes = progressEvent.loaded;
      this.progressBuilder.progress.bytes.done += newBytesDone;
      this.progressBuilder.refreshPercentage();
      this.progressBuilder.fireActivity('Uploading file', 'Uploading ' + this.rfile.title + ', part ' + (part.index + 1));
    };

    const axiosResponse = await this.options.axios.put(part.signedUrl, part.chunk, {
      cancelToken,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress
    });

    // We don't want to rely on receiving onUploadProgress events,
    // for all bytes sent, so now bump the remaining byte count.
    this.progressBuilder.progress.bytes.done += (part.chunk.byteLength - onUploadProgressBytes);

    return axiosResponse.headers.etag!;
  }


  private async uploadPart(part: PartUpload, cancelToken: CancelToken): Promise<string> {
    cancelToken.throwIfRequested();
    let response: string;
    if (this.options.putChunksWithNode === true) {
      // Using nodejs for PUTs is a workaround for #2229
      response = await this.putNode(part.signedUrl, new Uint8Array(part.chunk));
      // The putAxios upload gives progress updates during upload,
      // but putNode does not.
      this.progressBuilder.progress.bytes.done += part.chunk.byteLength;
    } else {
      response = await this.putAxios(part, cancelToken);
    }

    this.progressBuilder.refreshPercentage();
    this.progressBuilder.fireActivity('Uploading file', 'Uploaded part ' + (part.index + 1) + ' of ' + this.rfile.title);
    return response;
  }

  public cancel() {
    this.cancelTokenSource.cancel(`MultiPartUploader canceled ${this.rfile.title}`);
  }

}

type SignedUrl = {
  signedUrl: string,
  index: number,
};

type PartUpload = SignedUrl & {
  chunk: ArrayBuffer,
};
